import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../../api";
import { trackEvent } from "../../../../analytics/posthog";
import { useWordSave } from "../hooks/useWordSave";
import FeedbackCard from "../components/FeedbackCard";
import { AiSparkleIcon } from "../components/AiChatPopup";
import { useAiChat } from "../AiChatContext";
import s from "./StoryCinema.module.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HOLD_AFTER_AUDIO_MS = 1700;
const FALLBACK_SLIDE_MS = 5200;

const MUSIC_SRC = `${process.env.PUBLIC_URL || ""}/background_music.mp3`;
const MUSIC_VOL_BED = 0.14; // title, end screen, pauses between sentences
const MUSIC_VOL_DUCK = 0.045; // while the narrator is speaking

/* ─── Icons ─── */
function PlayIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
    </svg>
  );
}

function PauseIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

export default function StoryCinema({ storyId, moduleId = "story_recap", onClose, onAskAi }) {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | error | title | playing | end
  const [index, setIndex] = useState(0);
  const [runId, setRunId] = useState(0); // bump to restart the current slide
  const [paused, setPaused] = useState(false);
  const [slow, setSlow] = useState(false);
  const [showEn, setShowEn] = useState(true);
  const [musicOn, setMusicOn] = useState(true);
  const [progress, setProgress] = useState(0);
  const [audioFailed, setAudioFailed] = useState(false);
  const [avatarOk, setAvatarOk] = useState(true);
  const [activeWord, setActiveWord] = useState(null);
  const [wordAnchor, setWordAnchor] = useState(null); // viewport rect of the tapped word
  const [discovered, setDiscovered] = useState([]);
  const [holding, setHolding] = useState(false); // breath between scenes
  const [savedIndex, setSavedIndex] = useState(0); // resume point from a previous visit
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const hasStartedRef = useRef(false);
  const completedRef = useRef(false);

  const { isSaved, saveWord, removeWord, sparkleWordId } = useWordSave(storyId, data?.saved_word_ids);
  const { setModuleContext } = useAiChat() || {};

  const progressKey = `storyCinemaProgress:${storyId}`;

  const audioRef = useRef(null);
  const holdTimerRef = useRef(null);
  const phaseRef = useRef("audio"); // audio | hold
  const slowRef = useRef(false);
  const indexRef = useRef(0);
  useEffect(() => { indexRef.current = index; }, [index]);

  const musicRef = useRef(null);
  const musicFadeRef = useRef(null);
  const musicOnRef = useRef(true);
  const wordAudioRef = useRef(null);
  const enAudioRef = useRef(null);
  const wasAutoPausedRef = useRef(false);
  const showEnRef = useRef(showEn);
  useEffect(() => { showEnRef.current = showEn; }, [showEn]);

  /* ─── Background music: soft bed that ducks under the narration ─── */
  const fadeMusicTo = useCallback((target, ms = 700) => {
    const music = musicRef.current;
    if (!music) return;
    if (musicFadeRef.current) { clearInterval(musicFadeRef.current); musicFadeRef.current = null; }
    const start = music.volume;
    const delta = target - start;
    if (Math.abs(delta) < 0.005) { music.volume = target; return; }
    const steps = Math.max(1, Math.round(ms / 50));
    let n = 0;
    musicFadeRef.current = setInterval(() => {
      n++;
      music.volume = Math.min(1, Math.max(0, start + (delta * n) / steps));
      if (n >= steps) {
        clearInterval(musicFadeRef.current);
        musicFadeRef.current = null;
        if (target === 0) music.pause();
      }
    }, 50);
  }, []);

  const ensureMusic = useCallback((targetVol = MUSIC_VOL_BED) => {
    if (!musicOnRef.current) return;
    if (!musicRef.current) {
      const music = new Audio(MUSIC_SRC);
      music.loop = true;
      music.volume = 0;
      musicRef.current = music;
    }
    musicRef.current
      .play()
      .then(() => fadeMusicTo(targetVol, 1600))
      .catch(() => {}); // blocked until a user gesture — startShow retries
  }, [fadeMusicTo]);

  useEffect(() => () => {
    if (musicFadeRef.current) clearInterval(musicFadeRef.current);
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
  }, []);

  /* Lock page scroll while the cinema is open */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* Fetch story content */
  useEffect(() => {
    let alive = true;
    apiFetch(`/api/v1/dashboard/stories/${storyId}/cinema`)
      .then((res) => {
        if (!res.ok) throw new Error("Could not load the story");
        return res.json();
      })
      .then((d) => {
        if (alive) {
          setData(d);
          setStage("title");
          try {
            const saved = parseInt(localStorage.getItem(progressKey), 10);
            if (saved > 0) setSavedIndex(saved);
          } catch { /* private mode */ }
          ensureMusic(); // may be blocked pre-gesture; startShow retries
        }
      })
      .catch(() => { if (alive) setStage("error"); });
    return () => { alive = false; };
  }, [storyId, ensureMusic, progressKey]);

  /* Slides: topic acts as the prologue scene, then one scene per sentence */
  const slides = useMemo(() => {
    if (!data) return [];
    const list = [];
    if (data.topic) {
      list.push({
        sv: data.topic.in_swedish,
        en: data.topic.in_english,
        audioPath: "topic/topic_in_swedish.mp3",
        enAudioPath: "topic/topic_in_english.mp3",
        isTopic: true,
        words: data.topic.words || [],
      });
    }
    (data.sentences || []).forEach((sent) => {
      list.push({
        sv: sent.in_swedish,
        en: sent.in_english,
        audioPath: `sentences/${sent.id}/sentence_in_swedish.mp3`,
        enAudioPath: `sentences/${sent.id}/sentence_in_english.mp3`,
        words: sent.words || [],
      });
    });
    return list;
  }, [data]);

  // Sync AI chat context with current slide and selected word
  useEffect(() => {
    if (!setModuleContext) return;
    const currentSlide = slides[index] || null;
    const ctx = {
      origin: "cinema",
      story_topic: data?.topic
        ? { in_swedish: data.topic.in_swedish, in_english: data.topic.in_english }
        : undefined,
    };
    if (currentSlide && !currentSlide.isTopic) {
      ctx.sentence = { in_swedish: currentSlide.sv, in_english: currentSlide.en };
    }
    if (activeWord) {
      ctx.word = {
        word: activeWord.word,
        meaning: activeWord.meaning,
        description: activeWord.description,
        example: activeWord.example,
        exampleTranslation: activeWord.example_translation,
      };
    }
    setModuleContext(ctx);
  }, [data, slides, index, activeWord, setModuleContext]);

  const callStart = useCallback(() => {
    if (!moduleId || !storyId) return;
    apiFetch(`/api/v1/dashboard/modules/${storyId}/${moduleId}/start`, { method: "PATCH" }).catch(() => {});
  }, [storyId, moduleId]);

  const callComplete = useCallback(async () => {
    if (completedRef.current || !hasStartedRef.current || !moduleId || !storyId) return null;
    completedRef.current = true;
    try {
      const res = await apiFetch(`/api/v1/dashboard/modules/${storyId}/${moduleId}/complete`, { method: "PATCH" });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }, [storyId, moduleId]);

  const finish = useCallback(async () => {
    setStage("end");
    fadeMusicTo(MUSIC_VOL_BED, 1200); // music swells back for the end card
    try { localStorage.removeItem(progressKey); } catch { /* private mode */ }
    setSavedIndex(0);
    trackEvent("story_cinema_completed", { story_id: storyId });
    const result = await callComplete();
    if (result && !result.feeling) setShowFeedback(true);
  }, [storyId, fadeMusicTo, progressKey, callComplete]);

  const advance = useCallback(() => {
    if (indexRef.current >= slides.length - 1) finish();
    else setIndex(indexRef.current + 1);
  }, [slides.length, finish]);

  const advanceRef = useRef(advance);
  useEffect(() => { advanceRef.current = advance; }, [advance]);

  /* Core slide engine: play audio → reveal words via progress → hold → advance */
  useEffect(() => {
    if (stage !== "playing" || slides.length === 0) return undefined;

    setPaused(false);
    setProgress(0);
    setAudioFailed(false);
    setActiveWord(null);
    setWordAnchor(null);
    setHolding(false);
    wasAutoPausedRef.current = false;
    phaseRef.current = "audio";

    try { localStorage.setItem(progressKey, String(index)); } catch { /* private mode */ }

    const slide = slides[index];
    let disposed = false;

    const scheduleHold = (ms) => {
      phaseRef.current = "hold";
      holdTimerRef.current = setTimeout(() => {
        if (!disposed) advanceRef.current();
      }, ms);
    };

    const fail = () => {
      if (disposed) return;
      setAudioFailed(true);
      setProgress(1);
      fadeMusicTo(MUSIC_VOL_BED, 600);
      scheduleHold(FALLBACK_SLIDE_MS);
    };

    // Warm the cache for the next scene so cuts are seamless
    const next = slides[index + 1];
    if (next) {
      const prefetch = document.createElement("audio");
      prefetch.preload = "auto";
      prefetch.src = `${BACKEND}/api/v1/admin/audio/${storyId}/${next.audioPath}`;
    }

    const audio = new Audio(`${BACKEND}/api/v1/admin/audio/${storyId}/${slide.audioPath}`);
    audio.playbackRate = slowRef.current ? 0.7 : 1.0;
    audio.ontimeupdate = () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setProgress(audio.currentTime / audio.duration);
      }
    };
    audio.onended = () => {
      if (disposed) return;
      setProgress(1);
      fadeMusicTo(MUSIC_VOL_BED, 800); // let the music breathe between sentences

      const shouldPlayEn = showEnRef.current && data?.are_audio_lesson_generated && slide.enAudioPath;
      if (shouldPlayEn) {
        phaseRef.current = "en-audio";
        const enAudio = new Audio(`${BACKEND}/api/v1/admin/audio/${storyId}/${slide.enAudioPath}`);
        enAudioRef.current = enAudio;
        const onEnDone = () => {
          if (disposed) return;
          enAudioRef.current = null;
          setHolding(true);
          phaseRef.current = "hold";
          scheduleHold(HOLD_AFTER_AUDIO_MS);
        };
        enAudio.onended = onEnDone;
        enAudio.onerror = onEnDone;
        enAudio.play().catch(onEnDone);
      } else {
        setHolding(true);
        phaseRef.current = "hold";
        scheduleHold(HOLD_AFTER_AUDIO_MS);
      }
    };
    audio.onerror = fail;
    audio
      .play()
      .then(() => { if (!disposed) fadeMusicTo(MUSIC_VOL_DUCK, 350); }) // duck under the narrator
      .catch(fail);
    audioRef.current = audio;

    return () => {
      disposed = true;
      audio.pause();
      audio.ontimeupdate = null;
      audio.onended = null;
      audio.onerror = null;
      if (enAudioRef.current) { enAudioRef.current.pause(); enAudioRef.current = null; }
      if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
      audioRef.current = null;
    };
  }, [stage, index, runId, slides, storyId, fadeMusicTo, progressKey, data]);

  /* Controls */
  const togglePause = useCallback(() => {
    if (stage !== "playing") return;
    setPaused((p) => {
      const next = !p;
      if (next) {
        if (audioRef.current && !audioRef.current.paused) audioRef.current.pause();
        if (enAudioRef.current && !enAudioRef.current.paused) enAudioRef.current.pause();
        if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
        fadeMusicTo(MUSIC_VOL_BED, 800); // ambient music keeps the moment alive
      } else if (phaseRef.current === "audio" && audioRef.current) {
        audioRef.current.play().catch(() => {});
        fadeMusicTo(MUSIC_VOL_DUCK, 350);
      } else if (phaseRef.current === "en-audio" && enAudioRef.current) {
        enAudioRef.current.play().catch(() => {});
      } else {
        holdTimerRef.current = setTimeout(() => advanceRef.current(), 600);
      }
      return next;
    });
  }, [stage, fadeMusicTo]);

  const goPrev = useCallback(() => {
    if (stage !== "playing" || indexRef.current <= 0) return;
    setIndex(indexRef.current - 1);
  }, [stage]);

  const goNext = useCallback(() => {
    if (stage !== "playing") return;
    if (indexRef.current >= slides.length - 1) finish();
    else setIndex(indexRef.current + 1);
  }, [stage, slides.length, finish]);

  const startShow = useCallback(() => {
    ensureMusic(); // user gesture — guaranteed to unlock audio
    setIndex(0);
    setRunId((r) => r + 1);
    setStage("playing");
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      callStart();
    }
  }, [ensureMusic, callStart]);

  const resumeShow = useCallback(() => {
    ensureMusic();
    setIndex(Math.min(savedIndex, Math.max(slides.length - 1, 0)));
    setRunId((r) => r + 1);
    setStage("playing");
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      callStart();
    }
  }, [ensureMusic, savedIndex, slides.length, callStart]);

  const handleClose = useCallback(async () => {
    if (stage === "end" || stage === "feedback") { onClose(); return; }
    if (!hasStartedRef.current) { onClose(); return; }
    const result = await callComplete();
    if (result && !result.feeling) {
      setStage("feedback");
    } else {
      onClose();
    }
  }, [stage, callComplete, onClose]);

  const replayScene = useCallback(() => {
    if (stage !== "playing") return;
    setRunId((r) => r + 1); // restart the current scene from the top
  }, [stage]);

  const toggleMusic = useCallback(() => {
    setMusicOn((v) => {
      const next = !v;
      musicOnRef.current = next;
      if (next) {
        ensureMusic(phaseRef.current === "audio" && !audioRef.current?.paused ? MUSIC_VOL_DUCK : MUSIC_VOL_BED);
      } else {
        fadeMusicTo(0, 500);
      }
      return next;
    });
  }, [ensureMusic, fadeMusicTo]);

  /* ─── Word discovery ─── */
  const playWordAudio = useCallback((wordId, filename, rate = 1.0) => {
    if (!wordId) return;
    if (wordAudioRef.current) { wordAudioRef.current.pause(); wordAudioRef.current = null; }
    const audio = new Audio(`${BACKEND}/api/v1/admin/word-audio/${wordId}/${filename}`);
    audio.playbackRate = rate;
    wordAudioRef.current = audio;
    audio.play().catch(() => {});
  }, []);

  useEffect(() => () => {
    if (wordAudioRef.current) wordAudioRef.current.pause();
  }, []);

  const handleWordClick = useCallback((e, wordData) => {
    e.stopPropagation(); // don't toggle pause via the stage tap
    if (!paused) {
      togglePause();
      wasAutoPausedRef.current = true;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setWordAnchor({ x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
    setActiveWord(wordData);
    setDiscovered((prev) =>
      prev.some((w) => w.id === wordData.id) ? prev : [...prev, wordData]
    );
    trackEvent("story_cinema_word_viewed", { story_id: storyId, word: wordData.word });
  }, [paused, togglePause, storyId]);

  const closeWordCard = useCallback(() => {
    setActiveWord(null);
    setWordAnchor(null);
    setRemoveConfirm(false);
    if (wordAudioRef.current) { wordAudioRef.current.pause(); wordAudioRef.current = null; }
    if (wasAutoPausedRef.current) {
      wasAutoPausedRef.current = false;
      if (paused) togglePause(); // the story breathes back to life
    }
  }, [paused, togglePause]);

  const handleStageTap = useCallback(() => {
    if (activeWord) closeWordCard();
    else togglePause();
  }, [activeWord, closeWordCard, togglePause]);

  const toggleSlow = useCallback(() => {
    setSlow((v) => {
      slowRef.current = !v;
      if (audioRef.current) audioRef.current.playbackRate = !v ? 0.7 : 1.0;
      return !v;
    });
  }, []);

  /* Keyboard */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (activeWord) closeWordCard();
        else handleClose();
      }
      if (stage !== "playing") return;
      if (e.key === " ") { e.preventDefault(); handleStageTap(); }
      if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [stage, handleStageTap, goNext, goPrev, handleClose, activeWord, closeWordCard]);

  /* Derived rendering state */
  const slide = slides[index] || null;
  const tokens = useMemo(() => {
    const parts = (slide?.sv || "").split(/\s+/).filter(Boolean);
    const dict = slide?.words || [];
    return parts.map((text) => {
      const core = text.replace(/[^\p{L}\p{N}]/gu, "").toLowerCase();
      const wordData = core
        ? dict.find((w) => w.word?.toLowerCase() === core)
        : null;
      return { text, wordData };
    });
  }, [slide]);
  const visibleCount = audioFailed || progress >= 0.99
    ? tokens.length
    : Math.min(tokens.length, Math.ceil((progress / 0.85) * tokens.length));
  const enVisible = showEn && (audioFailed || progress > 0);
  const sceneCount = data?.sentences?.length || 0;
  const estMinutes = Math.max(1, Math.round(sceneCount * 0.5));
  const canResume = savedIndex > 0 && savedIndex < slides.length;

  const uniqueWordCount = useMemo(() => {
    const ids = new Set();
    slides.forEach((sl) => (sl.words || []).forEach((w) => { if (w.id != null) ids.add(w.id); }));
    return ids.size;
  }, [slides]);

  // Ambient gold dust drifting through the scenery
  const dust = useMemo(() =>
    Array.from({ length: 14 }, () => ({
      left: Math.random() * 100,
      size: 2 + Math.random() * 2.5,
      delay: Math.random() * 14,
      duration: 9 + Math.random() * 9,
      drift: -30 + Math.random() * 60,
    })), []);

  return createPortal(
    <div className={s.overlay}>
      {/* Background */}
      <div className={s.bgWrap} aria-hidden="true">
        {data?.has_avatar && avatarOk ? (
          <img
            className={s.bg}
            src={`${BACKEND}/api/v1/admin/story-avatar/${storyId}`}
            alt=""
            onError={() => setAvatarOk(false)}
          />
        ) : (
          <div className={s.bgFallback} />
        )}
        <div className={s.scrim} />
        <div className={s.vignette} />
      </div>
      {/* Ambient gold dust */}
      <div className={s.dustLayer} aria-hidden="true">
        {dust.map((p, i) => (
          <span
            key={i}
            className={s.dustMote}
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              "--drift": `${p.drift}px`,
            }}
          />
        ))}
      </div>

      <div className={s.letterboxTop} aria-hidden="true" />
      <div className={s.letterboxBottom} aria-hidden="true" />

      {/* Top bar */}
      <div className={s.topBar}>
        <button className={s.iconBtn} onClick={handleClose} title="Close" aria-label="Close cinema">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        {stage === "playing" && (
          <div className={s.segments}>
            {slides.map((_, i) => (
              <div key={i} className={s.segment}>
                <div
                  className={s.segmentFill}
                  style={{ width: i < index ? "100%" : i === index ? `${Math.min(progress, 1) * 100}%` : "0%" }}
                />
              </div>
            ))}
          </div>
        )}
        {stage === "playing" && onAskAi && (
          <button className={s.topAiBtn} onClick={onAskAi} aria-label="AI tutor">
            <AiSparkleIcon className={s.topAiBtnIcon} />
            <span>AI</span>
          </button>
        )}
      </div>

      {/* Loading */}
      {stage === "loading" && (
        <div className={s.centerScreen}>
          <div className={s.spinner} />
        </div>
      )}

      {/* Feedback stage (shown when X is pressed mid-story after starting) */}
      {stage === "feedback" && (
        <div className={s.centerScreen}>
          <div className={s.feedbackWrap}>
            <FeedbackCard storyId={storyId} moduleId={moduleId} onDone={onClose} />
          </div>
        </div>
      )}

      {/* Error */}
      {stage === "error" && (
        <div className={s.centerScreen}>
          <p className={s.errorText}>Could not load the story.</p>
          <button className={s.ghostBtn} onClick={onClose}>Close</button>
        </div>
      )}

      {/* Title screen */}
      {stage === "title" && data && (
        <div className={s.centerScreen}>
          <span className={`${s.eyebrow} ${s.item1}`}>Story Cinema</span>
          <h2 className={`${s.titleSv} ${s.item2}`}>{data.topic?.in_swedish || "Saga"}</h2>
          <p className={`${s.titleEn} ${s.item3}`}>{data.topic?.in_english || ""}</p>
          <div className={`${s.metaRow} ${s.item4}`}>
            <span>{sceneCount} scenes</span>
            <span className={s.metaDot} />
            <span>~{estMinutes} min</span>
            {uniqueWordCount > 0 && (
              <>
                <span className={s.metaDot} />
                <span className={s.metaGold}>{uniqueWordCount} words to discover</span>
              </>
            )}
          </div>
          <div className={`${s.titleActions} ${s.item5}`}>
            <button className={s.playBigBtn} onClick={canResume ? resumeShow : startShow}>
              <span className={s.playBigIcon}><PlayIcon size={22} /></span>
              <span className={s.playBigLabel}>
                <span className={s.playBigSv}>{canResume ? "Fortsätt" : "Spela upp"}</span>
                <span className={s.playBigEn}>
                  {canResume ? `Resume scene ${savedIndex}` : "Play the story"}
                </span>
              </span>
            </button>
            {canResume && (
              <button className={s.ghostBtn} onClick={startShow}>From the beginning</button>
            )}
          </div>
          <p className={`${s.hint} ${s.item6}`}>
            Sit back and listen — tap any glowing word to unlock its meaning.
          </p>
        </div>
      )}

      {/* Playing — the scene */}
      {stage === "playing" && slide && (
        <div className={s.stageArea} onClick={handleStageTap}>
          <div
            className={`${s.sceneText} ${holding && !paused ? s.sceneHold : ""}`}
            key={`scene-${index}-${runId}`}
          >
            {slide.isTopic && <span className={s.chapterTag}>Prolog</span>}
            <p className={s.sv}>
              {tokens.map((t, i) => {
                const isTappable = !!t.wordData;
                const isActive = isTappable && activeWord && activeWord.id === t.wordData.id;
                const isCurrent =
                  !paused && !holding && !audioFailed && progress < 0.99 && i === visibleCount - 1;
                return (
                  <span
                    key={i}
                    className={`${s.word} ${i < visibleCount ? s.wordOn : ""} ${isTappable ? s.wordTappable : ""} ${isActive ? s.wordActive : ""} ${isCurrent ? s.wordCurrent : ""}`}
                    onClick={isTappable ? (e) => handleWordClick(e, t.wordData) : undefined}
                  >
                    {t.text}
                  </span>
                );
              })}
            </p>
            <p className={`${s.en} ${enVisible ? s.enOn : ""}`}>{slide.en}</p>
          </div>
          {paused && !activeWord && (
            <div className={s.pausedBadge}>
              <PauseIcon size={12} />
              <span>Paused — tap to continue</span>
            </div>
          )}
        </div>
      )}

      {/* Word discovery card — popover anchored to the tapped word */}
      {stage === "playing" && activeWord && wordAnchor && (() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const cardW = Math.min(440, vw - 32);
        const left = Math.max(16, Math.min(wordAnchor.x - cardW / 2, vw - 16 - cardW));
        const fitsBelow = wordAnchor.bottom + 330 < vh;
        const cardStyle = fitsBelow
          ? { top: wordAnchor.bottom + 16, left, width: cardW }
          : { bottom: vh - wordAnchor.top + 16, left, width: cardW };
        const caretLeft = Math.max(18, Math.min(wordAnchor.x - left, cardW - 18));
        return (
          <div className={s.wordCard} style={cardStyle} onClick={(e) => e.stopPropagation()}>
            <span
              className={fitsBelow ? s.wordCaretTop : s.wordCaretBottom}
              style={{ left: caretLeft }}
              aria-hidden="true"
            />
            {/* Close */}
            <button className={s.wordCardClose} onClick={closeWordCard} title="Close">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
            {/* Hero: word + meaning + save */}
            <div className={s.wordCardHero}>
              <div className={s.wordCardHeroText}>
                <span className={s.wordCardWord}>{activeWord.word}</span>
                <span className={s.wordCardDivider} />
                <span className={s.wordCardMeaning}>{activeWord.meaning}</span>
              </div>
              <div className={s.wordCardSaveWrap}>
                <button
                  className={`${s.wordCardSaveBtn} ${isSaved(activeWord.id) ? s.wordCardSaveBtnSaved : ""}`}
                  onClick={() => {
                    if (isSaved(activeWord.id)) {
                      setRemoveConfirm(true);
                    } else {
                      saveWord(activeWord.id, "cinema");
                    }
                  }}
                  title={isSaved(activeWord.id) ? "Saved to My Words" : "Save to My Words"}
                >
                  {isSaved(activeWord.id) ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                      <path d="M5 12h14" />
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  )}
                </button>
                {sparkleWordId === String(activeWord.id) && (
                  <span className={s.sparkleRing} aria-hidden="true" />
                )}
              </div>
            </div>
            {/* Remove confirm */}
            {removeConfirm && (
              <div className={s.removeConfirm}>
                <span className={s.removeConfirmText}>Remove from My Words?</span>
                <div className={s.removeConfirmBtns}>
                  <button className={s.removeConfirmBtn} onClick={() => setRemoveConfirm(false)}>Keep</button>
                  <button
                    className={`${s.removeConfirmBtn} ${s.removeConfirmBtnDanger}`}
                    onClick={() => { removeWord(activeWord.id, "cinema"); setRemoveConfirm(false); }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
            {/* Audio row */}
            <div className={s.wordCardAudioRow}>
              <button className={s.wordCardAudioBtn} onClick={() => playWordAudio(activeWord.id, "word_in_swedish.mp3", 1.0)} title="Play word">
                <PlayIcon size={14} />
              </button>
              <button className={s.wordCardAudioBtn} onClick={() => playWordAudio(activeWord.id, "word_in_swedish.mp3", 0.7)} title="Slow play">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 7.5V18M15 7.5V18M3 16.811V8.69c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 0 1 0 1.954l-7.108 4.061A1.125 1.125 0 0 1 3 16.811Z" />
                </svg>
              </button>
            </div>
            {/* Description */}
            {activeWord.description && (
              <p className={s.wordCardDesc}>{activeWord.description}</p>
            )}
            {/* Example */}
            {activeWord.example && (
              <div className={s.wordCardExample}>
                <div className={s.wordCardExampleHeader}>
                  <span className={s.wordCardExampleLabel}>Exempel</span>
                  <button
                    className={s.wordCardAudioBtn}
                    onClick={() => playWordAudio(activeWord.id, "example_in_swedish.mp3", 1.0)}
                    title="Play example"
                  >
                    <PlayIcon size={12} />
                  </button>
                </div>
                <p className={s.wordCardExampleSv}>{activeWord.example}</p>
                <p className={s.wordCardExampleEn}>{activeWord.example_translation}</p>
              </div>
            )}
            <p className={s.wordCardHint}>Tap anywhere to continue the story</p>
          </div>
        );
      })()}

      {/* End screen */}
      {stage === "end" && (
        <div className={s.centerScreen}>
          <span className={`${s.eyebrow} ${s.item1}`}>Slut · The End</span>
          <h2 className={`${s.titleSv} ${s.item2}`}>Bra jobbat!</h2>
          <p className={`${s.titleEn} ${s.item3}`}>You just watched a whole story in Swedish.</p>
          {discovered.length > 0 && (
            <div className={`${s.discoveredWrap} ${s.item4}`}>
              <span className={s.discoveredLabel}>
                {discovered.length} {discovered.length === 1 ? "word" : "words"} discovered
              </span>
              <div className={s.discoveredChips}>
                {discovered.slice(0, 12).map((w) => (
                  <span key={w.id} className={s.discoveredChip}>{w.word}</span>
                ))}
              </div>
            </div>
          )}
          <div className={`${s.endActions} ${s.item5}`}>
            <button className={s.playBigBtn} onClick={startShow}>
              <span className={s.playBigIcon}><PlayIcon size={22} /></span>
              <span className={s.playBigLabel}>
                <span className={s.playBigSv}>Se igen</span>
                <span className={s.playBigEn}>Watch again</span>
              </span>
            </button>
            <button className={s.ghostBtn} onClick={onClose}>Done</button>
          </div>
          {showFeedback && (
            <div className={s.feedbackWrap}>
              <FeedbackCard storyId={storyId} moduleId={moduleId} onDone={onClose} />
            </div>
          )}
        </div>
      )}

      {/* Bottom controls */}
      {stage === "playing" && (
        <div className={s.controls} onClick={(e) => e.stopPropagation()}>
          <div className={s.controlsCluster}>
            <button
              className={`${s.iconBtn} ${index <= 0 ? s.iconBtnDisabled : ""}`}
              onClick={goPrev}
              disabled={index <= 0}
              title="Previous scene"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button className={s.playPauseBtn} onClick={togglePause} title={paused ? "Play" : "Pause"}>
              {paused ? <PlayIcon size={20} /> : <PauseIcon size={20} />}
            </button>
            <button className={s.iconBtn} onClick={replayScene} title="Replay this scene">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 2.64-6.36L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            <button className={s.iconBtn} onClick={goNext} title="Next scene">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
          <div className={s.controlsCluster}>
            <button
              className={`${s.pillBtn} ${slow ? s.pillBtnActive : ""}`}
              onClick={toggleSlow}
              title="Slow narration"
            >
              {slow ? "0.7×" : "1×"}
            </button>
            <button
              className={`${s.pillBtn} ${showEn ? s.pillBtnActive : ""}`}
              onClick={() => setShowEn((v) => !v)}
              title="English subtitles"
            >
              EN
            </button>
            <button
              className={`${s.pillBtn} ${musicOn ? s.pillBtnActive : ""}`}
              onClick={toggleMusic}
              title={musicOn ? "Mute music" : "Play music"}
              aria-label={musicOn ? "Mute background music" : "Play background music"}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13" />
                <circle cx="6" cy="18" r="3" />
                <circle cx="18" cy="16" r="3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
